/**
 * element-create-rules — the ELEMENT-CREATION half of React's host rules.
 *
 * BUG-RUNTIME-141 / CLAIM-SRC-0045. React does not build every host element
 * with `document.createElement`. Its `createInstance` switch
 * (react-dom 19.2.4 `ReactFiberConfigDOM.js`, the `createHoistableInstance` /
 * `createInstance` tag switch) has five arms, and only four of them are the
 * ordinary route:
 *
 *   svg    → createElementNS(SVG_NAMESPACE, tag)
 *   math   → createElementNS(MATH_NAMESPACE, tag)
 *   script → createElement('div'); div.innerHTML = '<script><\/script>';
 *            div.removeChild(div.firstChild)
 *   select → createElement('select') + multiple/size assignment
 *   default→ createElement(tag) (with `is` when the prop is a string)
 *
 * The `script` arm is the only one whose PURPOSE is inertness, and it is the
 * only one that is invisible to a DOM comparison. A script lifted out of a
 * parsed `innerHTML` carries the spec's **parser-inserted** flag, and a
 * parser-inserted script that is later moved into a document never executes.
 * A `document.createElement('script')` moved into the document DOES execute.
 * The two elements are byte-identical in `outerHTML`; only the side effect
 * differs — which is why 141 initial-DOM cases and a 2775-case browser sweep
 * were green for as long as this bug existed.
 *
 * Consequence when we get it wrong: any app that renders CMS- or user-supplied
 * markup as a React child gets script execution from JetShake that it does not
 * get from React. That is a difference in the SECURITY POSTURE of the
 * framework, not in its output.
 *
 * This module is the rule itself so that every element-creating writer on both
 * paths asks ONE question, per the BUG-RUNTIME-130 warning: a fix that
 * duplicates a table rather than sharing it will diverge again. The compiled
 * path calls it from `_el` / `_el$h` / the reactive host anchor; the compat
 * path calls it from `createDOMElement`.
 *
 * Scope, deliberately:
 *   - The namespace arms already live in `dom-utils.createDOMElement` (compat)
 *     and in the compiler's `_svg` emit (compiled), and they are OBSERVABLE in
 *     the DOM, so they are gated by the parity suites. They are not moved here.
 *   - `select`'s multiple/size assignment is a property write, not a creation
 *     route, and is handled by the form-control writers.
 *   - So `script` is the whole list. If React ever adds a second inert arm,
 *     it belongs in `INERT_CREATE_TAGS` and nowhere else.
 */
/**
 * Tags React deliberately creates off the ordinary `createElement` route in
 * order to make the resulting node INERT. Exported so a caller can decide
 * whether it needs the slower route before taking it.
 */
export const INERT_CREATE_TAGS = new Set(['script']);
/**
 * Build a `<script>` the way React does: parse one inside a throwaway `<div>`
 * and lift it out, so the node is flagged parser-inserted and can never run.
 *
 * Returns `null` — never throws — when the document cannot parse markup. The
 * preconditions are GUARDED rather than caught (docs/DANGER-ZONES.md: "an
 * error path that cannot be loud will eventually be expensive"): react-aria's
 * `CollectionBuilder` renders into a fake `Document` whose nodes have no
 * `innerHTML`, and that is an expected, cheap, silent fallback rather than an
 * exception thrown on a hot path.
 */
export function createInertScript(doc) {
    if (!doc || typeof doc.createElement !== 'function')
        return null;
    const div = doc.createElement('div');
    // A fake-document node has no string `innerHTML`; markup parsing is a
    // real-DOM affordance and the plain route is correct for everything else.
    if (!div || typeof div.innerHTML !== 'string')
        return null;
    // Split so no bundler, minifier or HTML-embedding of this file sees a
    // literal closing script tag.
    div.innerHTML = '<script><\/' + 'script>';
    const first = div.firstChild;
    if (!first || first.nodeType !== 1)
        return null;
    return div.removeChild(first);
}
/**
 * The ordinary creation route plus React's inert arms. `doc` may be any
 * document-like host (an iframe document, or a foreign/fake one).
 */
export function createHostElement(doc, tag, options) {
    if (INERT_CREATE_TAGS.has(tag)) {
        const inert = createInertScript(doc);
        if (inert)
            return inert;
    }
    return options ? doc.createElement(tag, options) : doc.createElement(tag);
}
